import React, { useEffect, useRef, useState } from "react";
import Button from "../components/base/Button";
import FormField from "../components/FormField";
import { Logo } from "../components/App";
import { errorMessage } from "../shared/utils/validators";
import * as Yup from "yup";
import { useFormik } from "formik";
import AuthService from "../services/AuthService";
import Icons from "../components/Icons";
import FormFieldGroup from "../components/FormFieldGroup";
import { Alert, Snackbar } from "@mui/material";
import { OAuthProviderButtons } from "./OAuthProviderButtons";
import "./authPages.css";

document.title = "Sign up — Orkaapps";

function SignUpPage() {
  const $firstName = useRef();
  const [visible, setVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isOtpView, setOtpView] = useState(false);
  const [mobileVerificationId, setMobileVerificationId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const schema = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required("Required first name")
      .max(50, "First name should not exceed 50 characters"),
    mobile: Yup.string()
      .required("Required mobile number")
      .matches(/^[0-9]{10}$/, "Enter a valid 10 digit mobile number"),
    password: Yup.string()
      .required("Required password")
      .min(6, "Password should be at least 6 characters")
      .max(25, "Password should not exceed 25 characters"),
  });

  const otpSchema = Yup.object().shape({
    otp: Yup.string()
      .required("OTP is required")
      .matches(/^[0-9]{4,6}$/, "Enter a valid OTP"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      mobile: "",
      password: "",
    },
    validationSchema: schema,
    onSubmit: handleGetStarted,
  });

  const otpFormik = useFormik({
    initialValues: { otp: "" },
    validationSchema: otpSchema,
    onSubmit: handleOtpVerify,
  });

  useEffect(() => {
    $firstName.current && $firstName.current.focus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get("oauth_error");
    if (oauthErr) {
      setSnackbar({
        open: true,
        message:
          "Google or Facebook sign-up did not complete. Configure OAuth on the server, or sign up with your number and password.",
        severity: "error",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  async function handleGetStarted(values) {
    setFormSubmitted(true);
    try {
      const res = await AuthService.signupVerificationCode(
        values.mobile.trim(),
        values.firstName.trim()
      );
      if (res.id?.mobileVerificationId) {
        setMobileVerificationId(res.id.mobileVerificationId);
      }
      setOtpView(true);
    } catch (error) {
      const message = errorMessage(error);
      if (message) {
        setSnackbar({
          open: true,
          message: message || "Something went wrong",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || "Something went wrong",
          severity: "error",
        });
      }
    }
  }

  async function handleOtpVerify({ otp }) {
    setFormSubmitted(true);
    try {
      const payload = {
        firstName: formik.values.firstName.trim(),
        lastName: formik.values.lastName.trim(),
        mobile: formik.values.mobile.trim(),
        password: formik.values.password.trim(),
        mobileVerificationCode: mobileVerificationId ? otp : null,
        mobileVerificationId,
      };
      await AuthService.createAccount(payload);
      window.location.replace("/");
    } catch (error) {
      setSnackbar({
        open: true,
        message: errorMessage(error) || "Something went wrong",
        severity: "error",
      });
    }
  }

  function snackBar() {
    return (
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-shell auth-shell--split">
        <aside className="auth-side">
          <span className="auth-side-tag">NEW TO ORKAAPS</span>
          <h2 className="auth-side-title">Business conversations made simple.</h2>
          <p className="auth-side-text">
            Build your WhatsApp workflow with campaigns, broadcasts and team collaboration in one place.
          </p>
          <ul className="auth-side-list">
            <li><span className="auth-dot" /> Run targeted campaigns quickly</li>
            <li><span className="auth-dot" /> Collaborate with your team securely</li>
            <li><span className="auth-dot" /> Scale messaging with confidence</li>
          </ul>
        </aside>
        <div className="auth-card">
          <Logo />
          <div className="auth-badge">{isOtpView ? "Verification" : "Create account"}</div>
          <h1 className="auth-title">{isOtpView ? "Verify OTP" : "Sign up"}</h1>
          <p className="auth-subtitle">
            {isOtpView
              ? `Enter the OTP sent to ${formik.values.mobile}`
              : "Create your account to start managing your WhatsApp workspace."}
          </p>

          {!isOtpView ? (
            <form onSubmit={formik.handleSubmit} className="auth-form">
              <FormFieldGroup>
                <FormField
                  ref={$firstName}
                  type="auth-text"
                  name="firstName"
                  placeholder="First Name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formSubmitted || formik.submitCount > 0)
                    && formik.touched.firstName
                    && formik.errors.firstName
                  }
                />
                <FormField
                  type="auth-text"
                  name="lastName"
                  placeholder="Last Name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formSubmitted || formik.submitCount > 0)
                    && formik.touched.lastName
                    && formik.errors.lastName
                  }
                />
              </FormFieldGroup>

              <div className="auth-field-wrap auth-focus-input">
                <FormField
                  type="auth-text"
                  name="mobile"
                  placeholder="WhatsApp Number"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formSubmitted || formik.submitCount > 0)
                    && formik.touched.mobile
                    && formik.errors.mobile
                  }
                />
              </div>

              <div className="auth-field-wrap auth-focus-input">
                <FormField
                  autoComplete="off"
                  name="password"
                  placeholder="Password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formSubmitted || formik.submitCount > 0)
                    && formik.touched.password
                    && formik.errors.password
                  }
                  type={visible ? "text" : "password"}
                  iconSuffix={
                    formik.values.password && (
                      <div
                        style={{ cursor: "pointer", display: "flex" }}
                        onClick={() => setVisible(!visible)}
                        aria-label={visible ? "Hide password" : "Show password"}
                      >
                        {visible ? Icons.Visible : Icons.VisibleOff}
                      </div>
                    )
                  }
                  maxLength={25}
                />
              </div>

              <p className="auth-terms">
                By continuing, you agree to our{" "}
                <a className="ui-link auth-terms-link" href="/terms">
                  Terms and conditions
                </a>{" "}
                and{" "}
                <a className="ui-link auth-terms-link" href="/privacy">
                  Privacy Policy
                </a>
                .
              </p>

              <Button
                type="submit"
                appearance="auth-btn"
                className="auth-primary-btn"
              >
                Get Started
              </Button>

              <OAuthProviderButtons />
            </form>
          ) : (
            <form onSubmit={otpFormik.handleSubmit} className="auth-form">
              <div className="auth-field-wrap auth-focus-input">
                <FormField
                  type="auth-text"
                  name="otp"
                  placeholder="Enter OTP"
                  value={otpFormik.values.otp}
                  onChange={otpFormik.handleChange}
                  onBlur={otpFormik.handleBlur}
                  error={
                    (formSubmitted || otpFormik.submitCount > 0)
                    && otpFormik.touched.otp
                    && otpFormik.errors.otp
                  }
                  style={{
                    textAlign: "center",
                    letterSpacing: "0.22em",
                  }}
                />
              </div>
              <Button
                type="submit"
                appearance="auth-btn"
                className="auth-primary-btn"
              >
                Verify and continue
              </Button>
            </form>
          )}

          <div className="auth-footer">
            Already have an account?
            <a href="/signin" className="ui-link auth-footer-link">
              Sign in
            </a>
          </div>
          {snackBar()}
        </div>
      </div>
    </div>
  );
}

export { SignUpPage };
